import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { RichTextContent } from "../components/common/RichTextContent";
import { useAuth } from "../context/AuthContext";
import { mutations } from "../lib/supabase/mutations";
import { queries } from "../lib/supabase/queries";
import { Profile, Task } from "../lib/types/database.types";
import { sendNewQuoteEmail } from "../utils/emailNotifications";

export const QuoteQueuePage: React.FC = () => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [hours, setHours] = useState("");
  const [pricePerHour, setPricePerHour] = useState("");
  const [error, setError] = useState<string | null>(null);
  const tasksQuery = useQuery({
    queryKey: ["tasks", "quote-queue", profile?.id],
    queryFn: () => queries.getTasksPendingQuote(profile?.id as string),
    enabled: Boolean(profile?.quote_first && profile.id),
  });
  const profilesQuery = useQuery({
    queryKey: ["profiles", "all"],
    queryFn: queries.getAllProfiles,
    enabled: Boolean(profile?.quote_first),
  });
  const quoteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedTask || !profile)
        throw new Error("Select a task and enter quote details.");
      const parsedHours = Number(hours);
      const parsedPrice = Number(pricePerHour);
      if (
        !Number.isFinite(parsedHours) ||
        parsedHours <= 0 ||
        !Number.isFinite(parsedPrice) ||
        parsedPrice < 0
      ) {
        throw new Error("Enter valid hours and a price per hour.");
      }
      const quote = await mutations.saveTaskQuote({
        task_id: selectedTask.id,
        hours: parsedHours,
        price_per_hour_zar: parsedPrice,
        status: "not_approved",
        created_by: profile.id,
      });
      const updatedTask = await mutations.updateTask(selectedTask.id, {
        status: "pending_finance_approval",
        estimated_hours: parsedHours,
      });
      const approvers = (profilesQuery.data || []).filter(
        (user: Profile) => user.is_finance_approver && user.email,
      );
      await Promise.allSettled(
        approvers.map((user) =>
          sendNewQuoteEmail(user.email as string, updatedTask, {
            hours: parsedHours,
            pricePerHourZar: parsedPrice,
            totalZar: Number(quote.total_zar),
          }),
        ),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["task-quotes"] });
      setSelectedTask(null);
      setHours("");
      setPricePerHour("");
    },
  });

  if (!profile?.quote_first) return <Navigate to="/dashboard" replace />;
  if (tasksQuery.isLoading || profilesQuery.isLoading)
    return <LoadingSpinner size="lg" text="Loading quote tasks..." />;
  if (tasksQuery.error || profilesQuery.error)
    return (
      <Card>
        <p className="text-red-700">Unable to load quote tasks.</p>
      </Card>
    );
  const total = (Number(hours) || 0) * (Number(pricePerHour) || 0);

  return (
    <div className="space-y-6">
      <Card
        header={
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Quotes</h1>
            <p className="text-sm text-slate-600">
              Prepare quotes for tasks awaiting pricing.
            </p>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-stone-300 text-slate-600">
                <th className="px-3 py-3">Task</th>
                <th className="px-3 py-3">App</th>
                <th className="px-3 py-3">Requested By</th>
                <th className="px-3 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {(tasksQuery.data || []).map((task) => (
                <tr key={task.id} className="border-b border-stone-200">
                  <td className="px-3 py-3 font-medium">{task.title}</td>
                  <td className="px-3 py-3">{task.related_app}</td>
                  <td className="px-3 py-3">{task.requested_by}</td>
                  <td className="px-3 py-3">
                    <Button
                      size="sm"
                      onClick={() => {
                        setError(null);
                        setPricePerHour(String(profile?.default_rate ?? 0));
                        setSelectedTask(task);
                      }}
                    >
                      Quote
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {tasksQuery.data?.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">
              No tasks are waiting for a quote.
            </p>
          )}
        </div>
      </Card>
      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-stone-300 bg-stone-100 p-6 shadow-xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">Quote task</h2>
                <p className="text-sm text-slate-600">{selectedTask.title}</p>
              </div>
              <button
                type="button"
                className="text-2xl text-slate-500"
                onClick={() => setSelectedTask(null)}
                aria-label="Close"
              >
                x
              </button>
            </div>
            <div className="shrink-0 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium">
                  Hours
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={hours}
                    onChange={(event) => setHours(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-stone-400 bg-white px-3 py-2"
                  />
                </label>
                <label className="block text-sm font-medium">
                  Price per hour (R)
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={pricePerHour}
                    onChange={(event) => setPricePerHour(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-stone-400 bg-white px-3 py-2"
                  />
                </label>
              </div>
              <div className="rounded-lg border border-stone-300 bg-stone-200 px-3 py-3 text-right text-sm">
                Total: <strong>R {total.toFixed(2)}</strong>
              </div>
              {(error || quoteMutation.error) && (
                <p className="text-sm text-red-700">
                  {error || (quoteMutation.error as Error).message}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setSelectedTask(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setError(null);
                    quoteMutation.mutate();
                  }}
                  isLoading={quoteMutation.isPending}
                >
                  Save Quote
                </Button>
              </div>
            </div>
            <div className="mt-5 min-h-0 space-y-3 overflow-y-auto rounded-lg border border-stone-300 bg-stone-200 p-3 text-sm">
              <div className="flex items-start justify-between gap-6">
                <div className="min-w-0">
                  <p className="font-medium text-slate-600">Task title</p>
                  <p className="break-words text-slate-900">{selectedTask.title}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-medium text-slate-600">Related app</p>
                  <p className="break-words text-slate-900">{selectedTask.related_app}</p>
                </div>
              </div>
              <div>
                <p className="font-medium text-slate-600">Description</p>
                <RichTextContent
                  html={selectedTask.description}
                  emptyText="No description provided."
                  className="text-slate-900"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
